<script setup lang="ts">
import { computed } from "vue";

const props = withDefaults(
  defineProps<{
    orientation?: "horizontal" | "vertical";
    variant?: "solid" | "dashed";
    spacing?: "none" | "sm" | "md" | "lg";
    label?: string;
  }>(),
  {
    orientation: "horizontal",
    variant: "solid",
    spacing: "md",
  }
);

const spacingClass = computed(() => {
  if (props.orientation === "vertical") {
    if (props.spacing === "none") return "";
    if (props.spacing === "sm") return "mx-px-8";
    if (props.spacing === "lg") return "mx-px-24";
    return "mx-px-16"; // md
  }
  if (props.spacing === "none") return "";
  if (props.spacing === "sm") return "my-px-8";
  if (props.spacing === "lg") return "my-px-24";
  return "my-px-16"; // md
});

const borderClass = computed(() => (props.variant === "dashed" ? "border-dashed border-slate-200" : "border-slate-200"));

// Vertical: label is silently ignored per spec
const showLabel = computed(() => !!props.label && props.orientation === "horizontal");
</script>

<template>
  <!-- Vertical divider -->
  <div
    v-if="orientation === 'vertical'"
    role="separator"
    aria-orientation="vertical"
    :class="['self-stretch border-l', borderClass, spacingClass]" />

  <!-- Horizontal with label -->
  <div
    v-else-if="showLabel"
    role="separator"
    :class="['gap-px-8 flex items-center', spacingClass]">
    <hr :class="['flex-1 border-t', borderClass]" />
    <span :class="['t-xs text-type-muted shrink-0']">{{ label }}</span>
    <hr :class="['flex-1 border-t', borderClass]" />
  </div>

  <!-- Horizontal, no label -->
  <hr
    v-else
    :class="['border-t', borderClass, spacingClass]" />
</template>
